// TeacherView.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  AppBar,
  Toolbar,
  Snackbar, // Import du composant Snackbar
  Alert as MuiAlert, // Renommage pour éviter les conflits
} from "@mui/material";
import LoopIcon from '@mui/icons-material/Loop';
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit"; // Import de l'icône d'édition
import teacherIcon from '../img/teacherIcon.png';
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import EditTeacherForm from "./EditTeacherForm"; // Import du nouveau composant
import InputAdornment from "@mui/material/InputAdornment";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { useTranslation } from "react-i18next";
import "../i18n";

const AlertSnackbar = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const TeacherView = ({ teachers, groups, setTeachers, setGroups, setStudents }) => {

  const { t } = useTranslation();

  const [filteredTeachers, setFilteredTeachers] = useState(teachers);
  const [loading, setLoading] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false); // État pour le modal d'édition
  const [teacherToEdit, setTeacherToEdit] = useState(null); // Professeur à éditer
  const [searchQuery, setSearchQuery] = useState(""); // Track search input

  // États pour les Snackbars
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // 'success' ou 'error'

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const teachersSnapshot = await getDocs(collection(db, "teachers"));
      setTeachers(
        teachersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          tutorType: doc.data()?.tutorType || "Teacher",
        }))
      );

      const groupsSnapshot = await getDocs(collection(db, "groups"));
      setGroups(
        groupsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );

      const studentsSnapshot = await getDocs(collection(db, "students"));
      setStudents(
        studentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );
    } catch (error) {
      console.error("Error loading data:", error);
      // Afficher une Snackbar d'erreur
      setSnackbarMessage(t("An error occurred while loading data. Please try again."));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, [setGroups, setStudents, setTeachers]);

  // Sync filteredTeachers avec teachers when teachers state updates
  useEffect(() => {
    setFilteredTeachers(teachers);
  }, [teachers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle search
  const handleSearch = (event) => {
    const query = (event.target.value || "").toLowerCase();
    setSearchQuery(query);

    setFilteredTeachers(
      (teachers || []).filter((teacher) => {
        const fn = (teacher.firstName || "").toLowerCase();
        const ln = (teacher.lastName || "").toLowerCase();
        const em = (teacher.email || "").toLowerCase();
        return fn.includes(query) || ln.includes(query) || em.includes(query);
      })
    );
  };

  // Delete teacher
  const handleDeleteTeacher = async (teacherId) => {
    const confirmDelete = window.confirm(
      t("Are you sure you want to delete this teacher? All related groups will also be deleted.")
    );
    if (!confirmDelete) return;

    try {
      setLoading(true);

      // Supprimer les groupes associés
      const teacherGroups = groups.filter((group) => group.teacherId === teacherId);
      const groupDeletePromises = teacherGroups.map((group) => deleteDoc(doc(db, "groups", group.id)));
      await Promise.all(groupDeletePromises);

      // Supprimer le professeur
      await deleteDoc(doc(db, "teachers", teacherId));

      // Mettre à jour les états locaux
      setGroups((prevGroups) => prevGroups.filter((group) => group.teacherId !== teacherId));
      setTeachers((prevTeachers) => prevTeachers.filter((teacher) => teacher.id !== teacherId));

      // Afficher une Snackbar de succès
      setSnackbarMessage(t("The teacher and related groups were deleted successfully."));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting teacher:", error);
      // Afficher une Snackbar d'erreur
      setSnackbarMessage(t("An error occurred while deleting the teacher. Please try again."));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // View teacher info
  const handleViewInfo = (teacher) => {
    setSelectedTeacher(teacher);
    setInfoModalOpen(true);
  };

  // Handle edit teacher
  const handleEditTeacher = (teacher) => {
    setTeacherToEdit(teacher);
    setEditModalOpen(true);
  };

  // Handler pour fermer la Snackbar
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
  <Box
    sx={{
      direction: "rtl",
      backgroundColor: "#fff",
      borderRadius: 3,
      border: "1px solid rgba(0,0,0,0.06)",
      boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
      overflow: "hidden",
    }}
  >
    {/* Header (same style as Students page) */}
    <Box
      sx={{
        px: 3,
        py: 2.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
      }}
    >
      {/* RIGHT: Title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          sx={{
            bgcolor: "#eef2ff",
            color: "#4f46e5",
            width: 46,
            height: 46,
            boxShadow: "0 8px 18px rgba(79,70,229,0.18)",
          }}
        >
          <PeopleAltIcon />
        </Avatar>

        <Box sx={{ textAlign: "right" }}>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
            {t("Teachers list")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {t("View and manage all teachers registered on the platform")}
          </Typography>
        </Box>
      </Box>

      {/* LEFT: Actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, direction: "ltr" }}>
        <IconButton
          onClick={fetchData}
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            border: "1px solid rgba(0,0,0,0.12)",
            backgroundColor: "#fff",
            "&:hover": { backgroundColor: "#f9fafb" },
          }}
        >
          <LoopIcon sx={{ fontSize: 22, color: "rgba(0,0,0,0.65)" }} />
        </IconButton>

          {/* Search row */}
          <TextField
            fullWidth
            size="small"
            value={searchQuery}
            onChange={handleSearch}
            placeholder={t("Search for a teacher by first name, last name, or email...")}
            sx={{
              maxWidth: 520,
              backgroundColor: "#f9fafb",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
      </Box>
    </Box>

    {/* Table */}
    <Paper elevation={0} sx={{ borderRadius: 0 }}>
      <Table sx={{ direction: "rtl" }}>
        <TableHead sx={{ bgcolor: "#ffffff" }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, textAlign: "right" }}> {t("Teacher name")}</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Phone")}</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Groups")}</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Students")}</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>{t("Actions")}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <CircularProgress size={26} />
                <Typography sx={{ mt: 1, color: "text.secondary" }}>
                  {t("Loading teachers list...")}
                </Typography>
              </TableCell>
            </TableRow>
          ) : filteredTeachers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                {t("No teachers match your search.")}
              </TableCell>
            </TableRow>
          ) : (
            filteredTeachers.map((teacher) => {
              const teacherGroups = groups.filter((g) => g.teacherId === teacher.id);
              const totalStudents = teacherGroups.reduce(
                (sum, g) => sum + (g.studentIds?.length || 0),
                0
              );

              return (
                <TableRow
                  key={teacher.id}
                  hover
                  sx={{
                    "& td": { py: 2 },
                    "&:hover": { backgroundColor: "#fafafa" },
                  }}
                >
                  {/* Name */}
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5}}>
                    <Avatar
                      sx={{
                        width: 38,
                        height: 38,
                        bgcolor: "rgba(79,70,229,0.10)",
                        color: "#4f46e5",
                        fontWeight: 800,
                      }}
                    >
                      {(teacher.firstName?.[0] || "أ").toUpperCase()}
                    </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                          {teacher.firstName} {teacher.lastName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {teacher.email || ""}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Phone */}
                  <TableCell sx={{ textAlign: "right", color: "rgba(0,0,0,0.7)" }}>
                    {teacher.phone || "-"}
                  </TableCell>

                  {/* Groups */}
                  <TableCell sx={{ textAlign: "right" }}>
                    <Chip
                      label={`${teacherGroups.length} ${t("groups")}`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(99,102,241,0.10)",
                        color: "#4f46e5",
                        fontWeight: 800,
                        borderRadius: "5px",
                      }}
                    />
                  </TableCell>

                  {/* Students */}
                  <TableCell sx={{ textAlign: "right" }}>
                    <Chip
                      label={`${totalStudents} ${t("students")}`}
                      size="small"
                      sx={{
                        bgcolor: totalStudents > 0 ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.18)",
                        color: totalStudents > 0 ? "rgba(22,163,74,0.95)" : "rgba(100,116,139,0.95)",
                        fontWeight: 800,
                        borderRadius: "5px",
                      }}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box sx={{ display: "flex", flexDirection: "center", mr: 2 }}>
                      <IconButton
                        onClick={() => handleEditTeacher(teacher)}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          border: "1px solid rgba(0,0,0,0.10)",
                          backgroundColor: "#fff",
                          "&:hover": { backgroundColor: "#f9fafb" },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Paper>

    {/* ===== EDIT DIALOG ===== */}
    {teacherToEdit && (
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            width: 760,
            maxWidth: "95vw",
            direction: "rtl",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <AppBar
          position="relative"
          color="default"
          elevation={0}
          sx={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <Typography sx={{ flexGrow: 1, fontWeight: 900, textAlign: "center" }}>
              {t("Edit teacher information")}
            </Typography>
            <IconButton onClick={() => setEditModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2.5, overflowY: "auto", flex: 1 }}>
          <EditTeacherForm
            teacher={teacherToEdit}
            onClose={() => setEditModalOpen(false)}
            setTeachers={setTeachers}
            teachers={teachers}
            groups={groups}
            setGroups={setGroups}
            setStudents={setStudents}
          />
        </Box>
      </Dialog>
    )}
  </Box>
    );

  };

export default TeacherView;
